// src/assets/js/quizzes/controllers/QuizzesController.js
// Orquesta: carga datos (via service), escucha View, muta State, decide qué renderizar.

import { initFavorites } from '../../common/favorites.js'; // ajusta ruta real
import { QuizzesState } from '../bom/QuizzesState.js';
import { QuizzesView } from '../ui/QuizzesView.js';

// De momento el Service lo dejaremos “inline” para no meter otro fichero aún.
// En el siguiente paso lo separamos a services/QuizzesIndexService.js (100% recomendado).
async function fetchIndexJson(indexUrl) {
	const res = await fetch(indexUrl, { cache: 'no-store' });
	if (!res.ok) throw new Error(`Failed to load index.json (${res.status})`);
	return res.json();
}

function flattenIndex(indexJson) {
	// indexJson: { total, subjects: [{ subjectId, subjectTitle, quizzes: [{quizId, quizTitle, region, regionId}] }] }
	const out = [];
	const subjects = Array.isArray(indexJson?.subjects) ? indexJson.subjects : [];

	for (const s of subjects) {
		const subjectId = s.subjectId;
		const subjectTitle = s.subjectTitle;
		const quizzes = Array.isArray(s.quizzes) ? s.quizzes : [];

		for (const q of quizzes) {
			out.push({
				subjectId,
				subjectTitle,
				quizId: q.quizId,
				quizTitle: q.quizTitle,
				region: q.region || null,
				regionId: q.regionId || null,
			});
		}
	}
	return out;
}

function buildRegionsAgg(flatItems) {
	const map = new Map(); // regionId -> { regionId, regionName, total }
	for (const item of flatItems) {
		const rid = item.regionId || 'sin_region';
		const rname = item.region || (rid === 'sin_region' ? 'Sin región' : rid);

		const prev = map.get(rid) || { regionId: rid, regionName: rname, total: 0 };
		prev.total += 1;
		map.set(rid, prev);
	}
	return Array.from(map.values()).sort((a, b) => b.total - a.total);
}

function filterItems(flatItems, { subjectId, regionId, query }) {
	let items = flatItems;

	if (subjectId) items = items.filter((x) => x.subjectId === subjectId);
	if (regionId) items = items.filter((x) => (x.regionId || null) === regionId);

	if (query) {
		const q = query.toLowerCase();
		items = items.filter((x) => {
			return (
				(x.quizTitle || '').toLowerCase().includes(q) ||
				(x.subjectTitle || '').toLowerCase().includes(q) ||
				(x.region || '').toLowerCase().includes(q)
			);
		});
	}

	return items;
}

export class QuizzesController {
	/**
	 * @param {object} params
	 * @param {HTMLElement} params.root
	 * @param {string} [params.indexUrl] default: ../static/quizzes/index.json
	 */
	constructor({ root, state = null, view = null, indexUrl = '../static/quizzes/index.json' } = {}) {
		if (!root) throw new Error('[QuizzesController] root is required');

		this.root = root;
		this.indexUrl = indexUrl;

		// Respeta datos existentes si los hay
		this.state = state || new QuizzesState({ viewMode: 'subjects' });
		this.view = view || new QuizzesView({ root });

		this._indexJson = null;
		this._flatItems = [];
		this.favs = null;
	}

	async init() {
		// 1) UI bindings
		this.view.bindUI();

		this._initFromURL();

		this.favs = initFavorites({
			root: document.querySelector('[data-quizzes-root]') || document,
			storageKey: 'selectivia:favs',
		});

		// 2) View -> State (event handlers)
		this.view.on('search', ({ query }) => this.state.setQuery(query));
		//this.view.on('query', ({ query }) => this.state.setQuery(query));
		this.view.on('subject', ({ subjectId }) => this.state.setSubject(subjectId));
		this.view.on('region', ({ regionId }) => this.state.setRegion(regionId));
		this.view.on('clear', () => this.state.resetAll());
		this.view.on('loadMore', () => this.state.loadMore());
		// vistas: subjects/list
		this.view.on('view', ({ viewMode }) => this.state.setViewMode(viewMode));

		//actions
		this.view.on('openSubject', ({ subjectId }) => {
			this.state.setSubject(subjectId);
		});
		this.view.on('openQuiz', ({ subjectId, quizId }) => window.alert('[not_implemented]'));

		// 3) State -> Render (subscribe)
		this._unsub = this.state.subscribe((snap, meta) => {
			if (!this._flatItems) return;
			this._renderFromState(snap);
			// ✅ sync solo tras acciones del usuario (no en meta init)
			if (meta?.type !== 'init') {
				this._syncURLFromState(snap);
			}
		});

		// 4) Load data
		this.view.setStatus('Cargando cuestionarios…');
		try {
			this._indexJson = await fetchIndexJson(this.indexUrl);
			this._flatItems = flattenIndex(this._indexJson);

			const subjects = Array.isArray(this._indexJson?.subjects) ? this._indexJson.subjects : [];
			this.view.setStatus('');

			// render inicial
			this.view.renderSubjectOptions(subjects);
			this._renderFromState(this.state.getSnapshot());
		} catch (err) {
			console.error(err);
			this.view.setStatus('No se pudo cargar el índice de cuestionarios.');
		}
	}

	destroy() {
		if (this._unsub) this._unsub();
	}

	/* =========================
	   Render decisions
	========================= */

	_renderFromState(snap) {
		const filters = this.state.getFilterParams();

		//const mustList = Boolean(filters.query || filters.subjectId);
		const mustList = Boolean(
			filters.query || filters.subjectId || window.__QUIZZES_TEST_MULT__ || window.__QUIZZES_FORCE_LIST__,
		);

		// =========================
		// LIST VIEW (quizzes)
		// =========================
		if (mustList) {
			const filtered = filterItems(this._flatItems, filters);

			const total = filtered.length;
			const visible = filtered.slice(0, snap.visibleCount);
			const loaded = visible.length;

			const vm = {
				viewMode: 'list',
				inputs: {
					query: snap.query,
					regionId: snap.regionId,
					subjectId: snap.subjectId, // sincroniza select Materia
				},

				pager: {
					loaded: visible.length,
					total: filtered.length,
					hasMore: loaded < total,
				},

				quizItems: visible,
				subjectCards: [],
			};

			this.view.render(vm);

			this.favs?.apply(this.view.root); //setea favoritos
			return;
		}

		// =========================
		// SUBJECTS VIEW (cards)
		// =========================
		const subjects = Array.isArray(this._indexJson?.subjects) ? this._indexJson.subjects : [];
		const regionId = filters.regionId;

		const subjectCards = subjects.map((s) => {
			const quizzes = Array.isArray(s.quizzes) ? s.quizzes : [];
			const filteredQuizzes = regionId ? quizzes.filter((q) => (q.regionId || null) === regionId) : quizzes;

			const latest = filteredQuizzes.slice(0, 3).map((q) => ({
				quizTitle: q.quizTitle,
				region: q.region || null,
				regionId: q.regionId || null,
				quizId: q.quizId || null,
			}));

			return {
				subjectId: s.subjectId,
				subjectTitle: s.subjectTitle,
				total: filteredQuizzes.length,
				latest,
			};
		});

		const finalCards = regionId ? subjectCards.filter((c) => c.total > 0) : subjectCards;

		const vm = {
			viewMode: 'subjects',
			inputs: {
				query: snap.query,
				regionId: snap.regionId,
				subjectId: snap.subjectId, // mantiene coherencia aunque sea null
			},
			// En subjects no hay paginación
			pager: {
				loaded: 0,
				total: 0,
				hasMore: false,
			},

			quizItems: [],
			subjectCards: finalCards,
		};

		this.view.render(vm);
		this.favs?.apply(this.view.root); //setea favoritos
	}

	_initFromURL() {
		const url = new URL(window.location.href);

		const subjectId = url.searchParams.get('subject') || null;
		const regionId = url.searchParams.get('region') || null;
		const query = url.searchParams.get('q') || '';

		return { subjectId, regionId, query };
	}

	_syncURLFromState(snap) {
		const url = new URL(window.location.href);

		if (snap.subjectId) url.searchParams.set('subject', snap.subjectId);
		else url.searchParams.delete('subject');

		if (snap.regionId) url.searchParams.set('region', snap.regionId);
		else url.searchParams.delete('region');

		if (snap.query) url.searchParams.set('q', snap.query);
		else url.searchParams.delete('q');

		// sin recargar
		window.history.replaceState({}, '', url);
	}
}
